// ============================================================
// SIMADCA v3.0 – GateKeeper.tsx
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';


const HASH_SHA256 = '1a9df7c539541cdd98af1483fe5926ab868e2cbe9cba866b9d859ab9fc93723c';


const SESSION_KEY = 'simadca_gate_v3';
const MAX_ATTEMPTS = 5;
const LOCKOUT_MS   = 10 * 60 * 1000; // 10 minute lockout după 5 încercări greșite

// ─── Funcție hash SHA-256 browser-native ─────────────────────
// Folosește WebCrypto API — rulează în browser fără biblioteci externe
// Nicio dependință, nicio expunere a secretului
async function sha256(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data    = encoder.encode(text);
  const hashBuf = await window.crypto.subtle.digest('SHA-256', data);
  const hashArr = Array.from(new Uint8Array(hashBuf));
  return hashArr.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ─── Verifică dacă sesiunea curentă e autorizată ─────────────
function isSessionAuthorized(): boolean {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return false;
    const { authorized, expires } = JSON.parse(raw);
    if (!authorized || Date.now() > expires) {
      sessionStorage.removeItem(SESSION_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

// ─── Salvează autorizarea în sesiune (8h) ────────────────────
function authorizeSession() {
  const payload = {
    authorized: true,
    expires:    Date.now() + 8 * 60 * 60 * 1000, // 8 ore
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
}

// ─── Load/save attempt tracking (localStorage — supraviețuiește refresh) ──
function loadAttempts(): { count: number; lockedUntil: number } {
  try {
    const raw = localStorage.getItem('simadca_gate_attempts');
    return raw ? JSON.parse(raw) : { count: 0, lockedUntil: 0 };
  } catch { return { count: 0, lockedUntil: 0 }; }
}
function saveAttempts(data: { count: number; lockedUntil: number }) {
  try { localStorage.setItem('simadca_gate_attempts', JSON.stringify(data)); } catch {}
}
function clearAttempts() {
  localStorage.removeItem('simadca_gate_attempts');
}

// ─────────────────────────────────────────────────────────────
// COMPONENT PRINCIPAL
// ─────────────────────────────────────────────────────────────

interface GateKeeperProps {
  children: React.ReactNode;
}

const GateKeeper: React.FC<GateKeeperProps> = ({ children }) => {
  const [authorized,  setAuthorized]  = useState(() => isSessionAuthorized());
  const [inputCode,   setInputCode]   = useState('');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [attempts,    setAttempts]    = useState(() => loadAttempts());
  const [countdown,   setCountdown]   = useState(0);
  const [showCode,    setShowCode]    = useState(false);

  // Countdown timer pentru lockout
  useEffect(() => {
    if (attempts.lockedUntil <= Date.now()) return;
    const tick = () => {
      const remaining = Math.ceil((attempts.lockedUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setCountdown(0);
        const reset = { count: 0, lockedUntil: 0 };
        setAttempts(reset);
        saveAttempts(reset);
        setError(null);
      } else {
        setCountdown(remaining);
      }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [attempts.lockedUntil]);

  const isLocked = attempts.lockedUntil > Date.now();

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLocked || loading || !inputCode.trim()) return;

    setLoading(true);
    setError(null);

    // Mică întârziere artificială — previne timing attacks și brute-force rapid
    await new Promise(r => setTimeout(r, 600 + Math.random() * 400));

    const inputHash = await sha256(inputCode.trim());
    const match     = inputHash === HASH_SHA256;

    if (match) {
      authorizeSession();
      clearAttempts();
      setAuthorized(true);
    } else {
      const newCount  = attempts.count + 1;
      const lockUntil = newCount >= MAX_ATTEMPTS ? Date.now() + LOCKOUT_MS : 0;
      const newData   = { count: newCount, lockedUntil: lockUntil };
      setAttempts(newData);
      saveAttempts(newData);

      if (newCount >= MAX_ATTEMPTS) {
        setError(`Prea multe încercări. Blocat ${Math.ceil(LOCKOUT_MS / 60000)} minute.`);
      } else {
        setError(`Cod incorect. ${MAX_ATTEMPTS - newCount} încercări rămase.`);
      }
      setInputCode('');
    }

    setLoading(false);
  }, [inputCode, isLocked, loading, attempts]);

  // Dacă e deja autorizat — redă copiii direct
  if (authorized) return <>{children}</>;

  // ─── UI Pagina de acces — light, identic cu restul aplicației ──
  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: '#f8fafc',
      fontFamily: "'Inter', sans-serif",
      padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>

        {/* Logo + titlu */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            marginBottom: 22,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 900, fontSize: 22, color: '#fff',
              boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
              userSelect: 'none',
            }}>
              S
            </div>
            <span style={{ fontWeight: 800, fontSize: 26, color: '#0f172a', letterSpacing: '-0.5px' }}>
              SIMADCA
            </span>
          </div>

          <h2 style={{
            fontSize: 24, fontWeight: 800, color: '#0f172a',
            margin: '0 0 6px', letterSpacing: '-0.4px',
          }}>
            Bun venit
          </h2>
          <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>
            Introduceți codul de acces pentru a continua
          </p>
        </div>

        {/* Card formular */}
        <div style={{
          background: '#fff', borderRadius: 16,
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
          padding: '28px 28px 24px',
        }}>

          {/* Error */}
          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 14px', borderRadius: 10, marginBottom: 16,
              background: '#fff5f5', border: '1px solid #fecaca',
            }}>
              <span style={{ fontSize: 15 }}>⚠️</span>
              <span style={{ fontSize: 13, color: '#ef4444', fontWeight: 500 }}>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">

            {/* Label */}
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Cod de acces
            </div>

            {/* Input */}
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <input
                type={showCode ? 'text' : 'password'}
                value={inputCode}
                onChange={e => { setInputCode(e.target.value); setError(null); }}
                placeholder={isLocked ? `Blocat ${countdown}s...` : '••••••••'}
                disabled={isLocked || loading}
                autoComplete="new-password"
                autoFocus
                style={{
                  width: '100%', boxSizing: 'border-box',
                  height: 44,
                  padding: '0 44px 0 14px',
                  background: '#fff',
                  border: `1.5px solid ${error ? '#fca5a5' : '#e2e8f0'}`,
                  borderRadius: 10,
                  color: '#0f172a', fontSize: 15,
                  fontFamily: 'inherit', outline: 'none',
                  letterSpacing: showCode ? 'normal' : '0.25em',
                  transition: 'border-color 0.15s',
                }}
                onFocus={e => { if (!error) e.target.style.borderColor = '#6366f1'; }}
                onBlur={e  => { if (!error) e.target.style.borderColor = '#e2e8f0'; }}
              />
              <button
                type="button"
                onClick={() => setShowCode(s => !s)}
                tabIndex={-1}
                style={{
                  position: 'absolute', right: 12, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#94a3b8', fontSize: 16, padding: 4,
                  display: 'flex', alignItems: 'center',
                }}
              >
                {showCode ? '🙈' : '👁️'}
              </button>
            </div>

            {/* Attempts dots */}
            {attempts.count > 0 && !isLocked && (
              <div style={{ display: 'flex', gap: 5, justifyContent: 'center', marginBottom: 14 }}>
                {[...Array(MAX_ATTEMPTS)].map((_, i) => (
                  <span key={i} style={{
                    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                    background: i < attempts.count ? '#ef4444' : '#e2e8f0',
                    transition: 'background 0.2s',
                  }} />
                ))}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isLocked || loading || !inputCode.trim()}
              style={{
                width: '100%', height: 46,
                borderRadius: 10, border: 'none',
                background: isLocked || !inputCode.trim()
                  ? '#f1f5f9'
                  : '#6366f1',
                color: isLocked || !inputCode.trim() ? '#94a3b8' : '#fff',
                fontSize: 14, fontWeight: 700,
                cursor: isLocked || !inputCode.trim() ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', transition: 'all 0.15s',
                boxShadow: isLocked || !inputCode.trim()
                  ? 'none'
                  : '0 2px 10px rgba(99,102,241,0.3)',
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 8,
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: 14, height: 14, borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    display: 'inline-block',
                    animation: 'gate-spin 0.6s linear infinite',
                  }} />
                  Se verifică...
                  <style>{`@keyframes gate-spin { to { transform: rotate(360deg); } }`}</style>
                </>
              ) : isLocked ? (
                `Deblocare în ${countdown}s`
              ) : (
                'Accesează platforma →'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p style={{
          textAlign: 'center', fontSize: 11,
          color: '#cbd5e1', marginTop: 20, lineHeight: 1.7,
        }}>
          INFOSEC CENTER SRL + INFLPR · Proiect PNTS SMIS 304244<br />
          © 2026 SIMADCA · v3.0
        </p>
      </div>
    </div>
  );
};

export default GateKeeper;